import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  InputAdornment,
  Chip,
  List,
  ListItem,
  ListItemText,
  Skeleton,
  Autocomplete,
  Link,
  Alert,
  Snackbar,
  Box,
  Typography,
} from '@mui/material';
import { Search as SearchIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import api, { enhancedApi } from '../api';

const CATEGORIES = [
  { key: 'all', label: 'All' },
  { key: 'tax_preparation', label: 'Tax' },
  { key: 'document_collection', label: 'Bookkeeping' },
  { key: 'client_onboarding', label: 'Onboarding' },
  { key: 'custom', label: 'Custom' },
];

const CUSTOM_CATEGORIES = ['general', 'compliance'];

const AssignWorkflowModal = ({ open, onClose, onSuccess }) => {
  const [step, setStep] = useState(1);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [templates, setTemplates] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [dueDate, setDueDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  useEffect(() => {
    if (open) {
      setStep(1);
      setSearch('');
      setCategory('all');
      setSelectedTemplate(null);
      setSelectedClient(null);
      setDueDate('');
      setError(null);
      setLoading(true);
      setTemplates([]);
      setClients([]);
      const fetchTemplates = async () => {
        try {
          const response = await api.get('/api/multi-tenant/workflow-templates/');
          const data = response.data?.results ?? response.data;
          setTemplates(Array.isArray(data) ? data : []);
        } catch {
          setError('Failed to load templates');
          setTemplates([]);
        } finally {
          setLoading(false);
        }
      };
      fetchTemplates();
    }
  }, [open]);

  useEffect(() => {
    if (open && step === 2 && clients.length === 0) {
      const fetchClients = async () => {
        try {
          const response = await enhancedApi.getClients();
          setClients(response.data || []);
        } catch {
          setError('Failed to load clients');
          setClients([]);
        }
      };
      fetchClients();
    }
  }, [open, step, clients.length]);

  const filteredTemplates = useMemo(() => {
    let list = templates;
    if (category !== 'all') {
      if (category === 'custom') {
        list = list.filter((t) => CUSTOM_CATEGORIES.includes(t.category));
      } else {
        list = list.filter((t) => t.category === category);
      }
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (t) =>
          (t.name || '').toLowerCase().includes(q) ||
          (t.description || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [templates, category, search]);

  const handleAssignClick = (template) => {
    setSelectedTemplate(template);
    setStep(2);
    setError(null);
  };

  const handleBack = () => {
    setStep(1);
    setSelectedTemplate(null);
    setSelectedClient(null);
    setDueDate('');
    setError(null);
  };

  const handleConfirm = async () => {
    if (!selectedTemplate || !selectedClient) return;
    setAssigning(true);
    setError(null);
    try {
      const payload = {
        template_id: selectedTemplate.id,
        client_id: selectedClient.id,
      };
      if (dueDate) payload.due_date = dueDate;
      await enhancedApi.assignWorkflowToClient(payload);
      setSnackbarOpen(true);
      onClose();
      if (onSuccess) onSuccess();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to assign workflow');
    } finally {
      setAssigning(false);
    }
  };

  const handleClose = () => {
    onClose();
    setStep(1);
    setSelectedTemplate(null);
    setSelectedClient(null);
    setError(null);
  };

  const getClientLabel = (client) =>
    client?.display_name ?? client?.name ?? `${client?.first_name || ''} ${client?.last_name || ''}`.trim() ?? '';

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>
          {step === 1 ? 'Assign Workflow to Client' : 'Assign to Client'}
        </DialogTitle>
        <DialogContent>
          {step === 1 ? (
            <Box>
              <TextField
                fullWidth
                size="small"
                placeholder="Search templates..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2 }}>
                {CATEGORIES.map((c) => (
                  <Chip
                    key={c.key}
                    label={c.label}
                    onClick={() => setCategory(c.key)}
                    color={category === c.key ? 'primary' : 'default'}
                    variant={category === c.key ? 'filled' : 'outlined'}
                    size="small"
                  />
                ))}
              </Box>
              {loading ? (
                <Box>
                  {[1, 2, 3, 4].map((i) => (
                    <Skeleton key={i} variant="rectangular" height={56} sx={{ mb: 1, borderRadius: 1 }} />
                  ))}
                </Box>
              ) : filteredTemplates.length === 0 ? (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    No templates found
                  </Typography>
                  <Link component={RouterLink} to="/workflows" variant="body2" onClick={handleClose}>
                    Create Template
                  </Link>
                </Box>
              ) : (
                <List disablePadding>
                  {filteredTemplates.map((template) => (
                    <ListItem
                      key={template.id}
                      divider
                      sx={{ py: 1.5 }}
                      secondaryAction={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            label={`${template.task_count ?? template.tasks?.length ?? 0} tasks`}
                            size="small"
                            variant="outlined"
                          />
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => handleAssignClick(template)}
                          >
                            Assign
                          </Button>
                        </Box>
                      }
                    >
                      <ListItemText
                        primary={template.name}
                        secondary={
                          template.description
                            ? template.description.length > 80
                              ? `${template.description.slice(0, 80)}...`
                              : template.description
                            : null
                        }
                        primaryTypographyProps={{ fontWeight: 600 }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          ) : (
            <Box sx={{ pt: 1 }}>
              {error && (
                <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
              <Autocomplete
                options={clients}
                getOptionLabel={getClientLabel}
                value={selectedClient}
                onChange={(_, value) => setSelectedClient(value)}
                renderInput={(params) => (
                  <TextField {...params} label="Select Client" size="medium" fullWidth />
                )}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Due Date"
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="medium"
                sx={{ mb: 1 }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {step === 2 ? (
            <>
              <Button onClick={handleBack} startIcon={<ArrowBackIcon />}>
                Back
              </Button>
              <Box sx={{ flex: 1 }} />
              <Button onClick={handleClose}>Cancel</Button>
              <Button
                variant="contained"
                onClick={handleConfirm}
                disabled={!selectedClient || assigning}
              >
                {assigning ? 'Assigning...' : 'Confirm Assignment'}
              </Button>
            </>
          ) : (
            <Button onClick={handleClose}>Cancel</Button>
          )}
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="success"
          variant="filled"
          sx={{ width: '100%' }}
        >
          Workflow assigned successfully
        </Alert>
      </Snackbar>
    </>
  );
};

export default AssignWorkflowModal;
